import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, unwrap } from "@/lib/api";
import type { Me } from "@/types/api";

const ROLE_HOME: Record<string, string> = {
  admin: "/admin/dashboard", manager: "/manager/dashboard", trader: "/trader/dashboard",
};

/** Admin "Login as user" — on success, hard-reload into the target's portal. */
export function useImpersonate() {
  return useMutation({
    mutationFn: async (userId: number) => unwrap<{ user: { id: number; role: string; fullName: string } }>((await api.post(`/auth/impersonate/${userId}`)).data),
    onSuccess: (d) => { window.location.href = ROLE_HOME[d.user.role] ?? "/"; },
  });
}

/** Exit impersonation — restore the admin session + reload to admin. */
export function useStopImpersonation() {
  return useMutation({
    mutationFn: async () => unwrap<{ user: { id: number; role: string } }>((await api.post("/auth/stop-impersonation")).data),
    onSuccess: () => { window.location.href = "/admin/dashboard"; },
  });
}

export function useMe() {
  return useQuery({
    queryKey: ["me"],
    queryFn: async () => {
      const { data } = await api.get("/auth/me");
      return unwrap<Me>(data);
    },
    staleTime: 60_000,
    retry: false,
  });
}

/**
 * RBAC permission check for the current user. `can(routeKey, "write")` →
 * may act (approve/edit) on that page; `"read"` → may view. Full-access users
 * and the loading state return true (fail-open — never hide before we know).
 */
export function useCan() {
  const me = useMe();
  const fullAccess = me.data?.fullAccess ?? true;
  const perms = me.data?.permissions ?? [];
  return (routeKey: string, level: "read" | "write" = "read") => {
    if (!me.data || fullAccess) return true;
    if (level === "read") return perms.includes(`${routeKey}:read`) || perms.includes(`${routeKey}:write`);
    return perms.includes(`${routeKey}:write`);
  };
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { email: string; password: string; totpCode?: string }) => {
      const { data } = await api.post("/auth/login", vars);
      return unwrap<{ token: string; user: Partial<Me> }>(data);
    },
    onSuccess: async () => {
      // /auth/login returns a slim user; invalidate so the next render
      // pulls the full Me payload from /auth/me.
      await qc.invalidateQueries({ queryKey: ["me"] });
    },
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await api.post("/auth/logout", {});
      return true;
    },
    onSuccess: () => {
      qc.removeQueries({ queryKey: ["me"] });
      qc.clear();
    },
  });
}

export function useRegister() {
  return useMutation({
    mutationFn: async (vars: {
      fullName: string;
      email: string;
      phone: string;
      gender?: string;
      dateOfBirth?: string;
      country?: string;
      state?: string;
      city?: string;
      zipCode?: string;
      address?: string;
      password: string;
      otpChannel: "email" | "whatsapp" | "telegram";
      referrerCode?: string;
    }) => {
      const { data } = await api.post("/auth/register", vars);
      return unwrap<{ userId: string; email: string; otpChannel: string; message: string }>(data);
    },
  });
}

/** Combined register-as-IB — creates user + ib_application in one POST. */
export function useRegisterIb() {
  return useMutation({
    mutationFn: async (vars: {
      fullName: string;
      email: string;
      phone: string;
      gender?: string;
      dateOfBirth?: string;
      country?: string;
      state?: string;
      city?: string;
      zipCode?: string;
      address?: string;
      password: string;
      otpChannel: "email" | "whatsapp" | "telegram";
      businessName?: string;
      businessType?: "sole_prop" | "company" | "partnership" | "individual";
      websiteUrl?: string;
      expectedMonthlyVolumeUsd?: number;
      marketingPlan?: string;
      declaredSponsorCode?: string;
      questionnaire?: { numberOfClients: string; clientSource: string; avgDeposit: string; hearAbout: string };
    }) => {
      const { data } = await api.post("/auth/register-ib", vars);
      return unwrap<{ userId: string; email: string; otpChannel: string; applicationId: number; existing?: boolean; message: string }>(data);
    },
  });
}

export function useActivate() {
  return useMutation({
    mutationFn: async (vars: { email: string; code: string }) => {
      const { data } = await api.post("/auth/activate", vars);
      return unwrap<{ activated: boolean }>(data);
    },
  });
}

export function useResendOtp() {
  return useMutation({
    mutationFn: async (vars: { email: string; purpose?: "activation" | "password_reset" }) => {
      const { data } = await api.post("/auth/otp/resend", vars);
      return unwrap<{ sent: boolean }>(data);
    },
  });
}
